import * as React from 'react';
import { toast } from 'react-toastify';
import ScheduleDayData from './ScheduleDay';
import ScheduleNewSearchData from './ScheduleNewSearch';
import * as DateHelpers from './DateHelpers';
import type { GenericList, PublicHoliday, ShutdownPeriod } from './models/JobSharedInterfaces';
import { CheckPermissions, GetQuery, PostQuery } from './Shared';
import { useNavigate } from 'react-router-dom';
import { useRef } from 'react';
import type { ItemOrder, ScheduleDay } from './models/JobScheduleInterfaces';

interface SaveScheduleJob {
    id: number;
    orderManual: string;
    jobOrder: number;
    onOffhireDate: string | null;
    purchaseOrderNumber: string;
    accountChecked: boolean;
    goAhead: boolean;
    rowVersionTimestamp: Uint8Array
    itemIndex: number;
}

const ScheduleNewData = () => {
    let navigate = useNavigate();
    const [loading, setLoading] = React.useState(true);
    const [loadingDay, setLoadingDay] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [monthOptions, setMonthOptions] = React.useState<GenericList[]>([]);
    const [yearOptions, setYearOptions] = React.useState<GenericList[]>([]);
    const [scheduleBranchOptions, setScheduleBranchOptions] = React.useState<GenericList[]>([]);
    const [monthAmountFormatted, setMonthAmountFormatted] = React.useState("$0.00");
    const [monthlyBudget, setMonthlyBudget] = React.useState(0);
    const [monthlyBudgetFormatted, setMonthlyBudgetFormatted] = React.useState("$0.00");
    const [month, setMonth] = React.useState(1);
    const [year, setYear] = React.useState(2000);
    const [scheduleDate, setScheduleDate] = React.useState("");
    const [branchFilterId, setBranchFilterId] = React.useState(1);
    const [noteTypes, setNoteTypes] = React.useState<GenericList[]>([]);
    const [scheduleDays, setScheduleDays] = React.useState<ScheduleDay[]>([]);
    const [allowEdit, setAllowEdit] = React.useState(true);
    const [allowAllocateToPastDate, setAllowAllocateToPastDate] = React.useState(false);
    const [allowUpdateDayLimits, setAllowUpdateDayLimits] = React.useState(false);
    const [allowUpdateMonthlyBudget, setAllowUpdateMonthlyBudget] = React.useState(false);
    const [allowLock, setAllowLock] = React.useState(false);
    const [allowGetAccountBalance, setAllowGetAccountBalance] = React.useState(false);
    const [publicHolidays, setPublicHolidays] = React.useState<PublicHoliday[]>([]);
    const [shutdownPeriods, setShutdownPeriods] = React.useState<ShutdownPeriod[]>([]);
    const [reasonOptions, setReasonOptions] = React.useState<GenericList[]>([]);
    const [scheduleTypes, setScheduleTypes] = React.useState<GenericList[]>([]);
    const [statusOptions, setStatusOptions] = React.useState<GenericList[]>([]);
    const [scheduleWarningNo, setScheduleWarningNo] = React.useState(3);
    const [scheduleBookedNo, setScheduleBookedNo] = React.useState(5);
    const [showSearch, setShowSearch] = React.useState(false);
    const [scrollScreen, setScrollScreen] = React.useState(false);
    const scrollRef = useRef<HTMLDivElement | null>(null); 

    React.useEffect(() => {
        var date = new Date();
        var currentMonth = (date.getMonth() + 1);
        var currentYear = date.getFullYear();
        setMonth(currentMonth);
        setYear(currentYear);

        getData();
    }, []);

    const getData = async () => {
        var hasPermission = await CheckPermissions("CreateEditSchedule");
        if (hasPermission) {
            getScheduleScreen();
        } else {
            navigate("/accessdenied");
        }
    }

    const getScheduleScreen = async () => { 
        var today = DateHelpers.getCurrentDate();
        const apiUrl = "GetScheduleAll?scheduleDate=" + today + "&getMultiple=true&scheduleBranchId=" + branchFilterId + '&CompanyId=2';
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            let scheduleDays = setScheduleData(result.data.schedule);
            setScheduleDays(scheduleDays);
            setBranchFilterId(result.data.scheduleBranchId);
            setMonthAmountFormatted(result.data.monthAmountFormatted);
            setMonthlyBudget(result.data.monthlyBudget);
            setMonthlyBudgetFormatted(result.data.monthlyBudgetFormatted);
            setMonthOptions(result.data.monthOptions);
            setYearOptions(result.data.yearOptions);
            setScheduleBranchOptions(result.data.scheduleBranchOptions);
            setScheduleWarningNo(result.data.scheduleWarningNo);
            setScheduleBookedNo(result.data.scheduleBookedNo);
            setNoteTypes(result.data.noteTypes);
            setAllowEdit(result.data.allowEdit);
            setAllowAllocateToPastDate(result.data.allowAllocateToPastDate);
            setAllowUpdateDayLimits(result.data.allowUpdateDayLimits);
            setAllowUpdateMonthlyBudget(result.data.allowUpdateMonthlyBudget);
            setAllowLock(result.data.allowLock);
            setAllowGetAccountBalance(result.data.allowGetAccountBalance);
            setPublicHolidays(result.data.publicHolidays);
            setShutdownPeriods(result.data.shutdownPeriods);
            setReasonOptions(result.data.reasonOptions);
            setScheduleTypes(result.data.scheduleTypes);
            setStatusOptions(result.data.statusOptions);
            setLoading(false);
            
            setScrollScreen(true);
        }       
    }

    React.useEffect(() => {

        if (scrollScreen && scrollRef && scrollRef.current) {
            const rect = scrollRef.current.getBoundingClientRect();
            var header = document.getElementById('header');
            var headerHeight = 0;
            if (header) {
                headerHeight = header.offsetHeight;
            }

            const element = document.getElementById("screen-container");
            if (element) {
                var options: ScrollToOptions = {
                    behavior: "smooth",
                    left: 0,
                    top: rect.top - headerHeight - 50
                };
                element.scrollTo(options);
            }
            setScrollScreen(false);
        }
    }, [scrollScreen]);

    const getSchedule = async (schedDate: string, getMultiple: boolean, scrollToDay: boolean) => {
        setLoadingDay(true);

        const apiUrl = "GetSchedule?scheduleDate=" + schedDate + "&getMultiple=" + getMultiple + "&scheduleBranchId=" + branchFilterId + '&CompanyId=2';
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            let schedule = result.data.schedule;
            let scheduleDays = setScheduleData(schedule);

            setScheduleDays(scheduleDays);
            setMonthAmountFormatted(result.data.monthAmountFormatted);
            setMonthlyBudget(result.data.monthlyBudget);
            setMonthlyBudgetFormatted(result.data.monthlyBudgetFormatted);
            setLoadingDay(false);
            setIsLoading(false);

            if (scrollToDay) {
                setScrollScreen(true);
            }
        } 
    }

    const setScheduleData = (scheduleDays: ScheduleDay[]) => {
        for (var x = 0; x < scheduleDays.length; x++) {
            let jobs = scheduleDays[x].jobs;
            var jobIndex = 1;
            for (var i = 0; i < jobs.length; i++) {
                let job = jobs[i];
                job.itemIndex = i;
                if (job.totalItems === 0) {
                    job.jobIndex = jobIndex;
                    jobIndex += 1;
                } else {
                    job.jobIndex = jobIndex;  //not used but can't be null else errors
                }
                var items = job.items;
                for (var y = 0; y < items.length; y++) {
                    items[y].jobIndex = jobIndex;
                    items[y].itemIndex = y;
                    jobIndex += 1;
                }
            }
        }
        return scheduleDays;
    }

    const refresh = () => {
        var schedDate = DateHelpers.getCurrentDate();
        var getMultiple = true;
        if (scheduleDate != null && scheduleDate != "") {
            //get single date
            schedDate = scheduleDate;
            getMultiple = false;
            setScheduleDays([]);
        } else {
            var checkDate = new Date(schedDate); //todays date
            if (((checkDate.getMonth() + 1) != month) || (checkDate.getFullYear() != year)) {
                //use the first of the month
                var newdate = DateHelpers.get1st(month, year);

                //update date to start date
                schedDate = newdate;
            }

            setScheduleDays([]);
        }

        getSchedule(schedDate, getMultiple, false);
    }

    //SEARCH
    const search = () => {
        setShowSearch(true);
    }

    const hideSearchModal = () => {
        setShowSearch(false);
    }

    const viewSearchDay = (searchDate: string) => {
        setShowSearch(false);
        setScheduleDate(searchDate);
        setScheduleDays([]);
        getSchedule(searchDate, false, false);
    }

    const resetSearch = () => {
        var schedDate = DateHelpers.getCurrentDate();
        var checkDate = new Date(schedDate); //todays date
        var startDate = schedDate;
        var dayCalc = 2;
        var dayofWeek = DateHelpers.getDayOfWeek(startDate);
        if (dayofWeek === 4 || dayofWeek === 5 || dayofWeek === 6) {
            dayCalc = 4;
        } else if (dayofWeek === 0) {
            dayCalc = 3;
        }
        var currentMonth = (checkDate.getMonth() + 1);
        var currentYear = checkDate.getFullYear();
        
        setScheduleDate("");
        setMonth(currentMonth);
        setYear(currentYear);
        setScheduleDays([]);

        getSchedule(schedDate, true, true);
    }

    const updateSearchScheduleDays = (datesToUpate: string[]) => {
        //update all the days where we rescheduled items via the search screen
        for (var i = 0; i < datesToUpate.length; i ++) {
            updateDayData(datesToUpate[i], true);
        }
    }

    //END SEARCH

    //ADD NEW / UPDATE
    const updateScheduleDay = (schedDate: string, scheduleDay: ScheduleDay) => {
        var updatedScheduleDays = [...scheduleDays];
        var existDayIndex = updatedScheduleDays.findIndex(sd => sd.scheduledDate === schedDate);
        if (existDayIndex >= 0) {
            scheduleDay.scheduleErrors = [];    //schedule day has been updated so clear any errors
            updatedScheduleDays[existDayIndex] = scheduleDay;
        }
        setScheduleDays(updatedScheduleDays);
    }

    const updateDayData = (schedDate: string, refreshDayOnly: boolean) => {
        updateScheduleDayData(schedDate, refreshDayOnly);
    }

    const updateScheduleDayData = async (schedDate: string, refreshDayOnly: boolean) => {
        //check if day is loaded before trying to update
        var existingDayIndex = scheduleDays.findIndex(sd => sd.scheduledDate === schedDate);
        if (existingDayIndex < 0) {
            return;
        }
        //can't set state after this as it reloads the page and changes the scroll position
        var existScrollPos = document.documentElement.scrollTop || document.body.scrollTop;

        let getMultiple = true;
        if (refreshDayOnly || (schedDate != null && schedDate != "")) {
            //get single date
            getMultiple = false;
        }

        var jobShowItems: number[] = [];
        var day = scheduleDays.find(d => d.scheduledDate === schedDate);
        if (day) {
            //get any open jobs
            var jobs = day.jobs.filter(j => j.showItems);
            for (var i = 0; i < jobs.length; i++) {
                //add any sub-jobs just in case they delete the parent job
                var items = jobs[i].items;
                for (var x = 0; x < items.length; x++) {
                    jobShowItems.push(jobs[i].items[x].id);
                }
            }
        }

        const apiUrl = "GetScheduleAll?scheduleDate=" + schedDate + "&getMultiple=" + getMultiple + "&scheduleBranchId=" + branchFilterId + '&CompanyId=2';
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            let newScheduleDays = setScheduleData(result.data.schedule);

            var days = [...scheduleDays];
            var currDay = days.find(s => s.scheduledDate === schedDate);
            if (refreshDayOnly && newScheduleDays.length > 0) {
                //we need to just replace the single date
                currDay = newScheduleDays[0];
                var currDayIndex = days.findIndex(s => s.scheduledDate === schedDate);
                //re-open any items for jobs that were set to open
                reOpenDays(currDay, jobShowItems);

                //replace data with updated data for selected day
                replaceAtIndex(currDayIndex, currDay);
            } else {
                currDay = newScheduleDays.find(s => s.scheduledDate === schedDate);
                if (currDay) {
                    reOpenDays(currDay, jobShowItems);
                }
                setScheduleDays(newScheduleDays);
            }

            setMonthOptions(result.data.monthOptions);
            setYearOptions(result.data.yearOptions);
            setScheduleBranchOptions(result.data.scheduleBranchOptions);
            setNoteTypes(result.data.noteTypes);
            setAllowEdit(result.data.allowEdit);
            setAllowAllocateToPastDate(result.data.allowAllocateToPastDate);
            setLoading(false);
            setLoadingDay(false);

            window.scrollTo(0, existScrollPos);
        }  
    }

    const replaceAtIndex = (currDayIndex: number, currDay: ScheduleDay) => {
        setScheduleDays(prevItems =>
            prevItems.map((item, idx) => (idx === currDayIndex ? currDay : item))
        );
    };

    const reOpenDays = (currDay: ScheduleDay, jobShowItems: number[]) => {
        if (currDay && jobShowItems.length > 0) {
            var currJobs = currDay.jobs;
            for (var y = 0; y < jobShowItems.length; y++) {
                var job = currJobs.find(s => s.id == jobShowItems[y]);
                if (job) {
                    job.showItems = true;
                }
            }
        }
    }

    const saveDayOrder = (schedDate: string, items: ItemOrder[]) => {
        saveOrder(schedDate, items);
    }

    const saveOrder = async (schedDate: string, items: ItemOrder[]) => {
        var jsonItem = JSON.stringify(items);

        const apiUrl = "SaveScheduleItemOrder";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving order!");
        } else {
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                updateDayData(schedDate, true);
                toast.success("Schedule Saved");
            }
        }
    }

    const saveDayData = (schedDate: string) => {
        saveDay(schedDate);
    }

    const saveDay = async (schedDate: string) => {
        var existDayIndex = scheduleDays.findIndex(sd => sd.scheduledDate === schedDate);
        if (existDayIndex >= 0) {
            var scheduleDay = scheduleDays[existDayIndex];
            var changedJobs = scheduleDay.jobs.filter(s => s.isDirty);

            let saveJobs: SaveScheduleJob[] = [];
            for (var i = 0; i < changedJobs.length; i++) {
                let changedJob = changedJobs[i];

                if (changedJob.totalItems === 0) {
                    //save job
                    let saveJob: SaveScheduleJob = {
                        id: changedJob.id,
                        itemIndex: changedJob.itemIndex,
                        orderManual: changedJob.orderManual,
                        jobOrder: changedJob.jobOrder,
                        purchaseOrderNumber: changedJob.purchaseOrderNumber,
                        onOffhireDate: changedJob.onOffhireDate == "" ? null : changedJob.onOffhireDate,
                        accountChecked: changedJob.accountChecked,
                        goAhead: changedJob.goAhead,
                        rowVersionTimestamp: changedJob.rowVersionTimestamp
                    };
                    saveJobs.push(saveJob);
                } else {
                    //save all items
                    for (var y = 0; y < changedJob.items.length; y++) {
                        let changedItem = changedJob.items[y];

                        let saveJob: SaveScheduleJob = {
                            id: changedItem.id,
                            itemIndex: changedItem.itemIndex,
                            orderManual: changedJob.orderManual,    //this one at job level so save for all items
                            jobOrder: changedItem.jobOrder,
                            purchaseOrderNumber: changedItem.purchaseOrderNumber,
                            onOffhireDate: changedItem.onOffhireDate == "" ? null : changedItem.onOffhireDate,
                            accountChecked: changedItem.accountChecked,
                            goAhead: changedItem.goAhead,
                            rowVersionTimestamp: changedItem.rowVersionTimestamp
                        };
                        saveJobs.push(saveJob);
                    }
                } 
            }

            if (saveJobs.length > 0) {
                var schedule = {
                    scheduleDate: schedDate,
                    items: saveJobs
                };

                var jsonItem = JSON.stringify(schedule);

                const apiUrl = "SaveScheduleItems";
                var result = await PostQuery(apiUrl, jsonItem);
                if (result.error) {
                    toast.error("Error saving schedule!");
                } else {
                    if (result.data.isError) {
                        var errors = result.data.messages as any[];
                        var scheduleErrors: string[] = [];
                        for (var i = 0; i < errors.length; i++) {
                            toast.error(errors[i].content);
                            scheduleErrors.push(errors[i].content);
                        };
                        scheduleDay.scheduleErrors = scheduleErrors;
                        setScheduleDays(scheduleDays);
                    } else {
                        updateDayData(schedDate, true);
                        toast.success("Schedule Saved");
                    }
                }
            } else {
                toast.info("No items have changed");
            }
        } else {
            toast.error("Cannot find day to update");
        }
    }

    //END ADD NEW / UPDATE

    //ALLOCATE
    const goAllocate = (schedDate: string) => {
        navigate("/allocate/" + schedDate);
    }
    //END ALLOCATE

    const handleMonthChange = (e: any) => {
        setMonth(e.target.value * 1);
        setScheduleDate("");
    }

    const handleYearChange = (e: any) => {
        setYear(e.target.value * 1);
        setScheduleDate("");
    }

    const handleBranchChange = (e: any) => {
        setBranchFilterId(e.target.value);
    }

    const handleBudgetChange = (e: any) => {
        var budget = e.target.value * 1;
        saveMonthlyBudget(budget);
    }

    const saveMonthlyBudget = async (budget: number) => {
        var monthlyBudget = {
            Month: month,
            Year: year,
            Budget: budget
        };

        var jsonItem = JSON.stringify(monthlyBudget);

        const apiUrl = "SaveMonthlyBudget";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving budget!");
        } else {
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                setMonthlyBudget(budget);
                setMonthlyBudgetFormatted(result.data.budgetFormatted);
                toast.success("Monthly Budget Saved");
            }
        }
    }

    let scheduleSearch = <ScheduleNewSearchData statusOptions={statusOptions} showModal={showSearch} hideModal={hideSearchModal} view={viewSearchDay} updateScheduleDay={updateSearchScheduleDays} />

    const renderDetails = (
        <div>
            <div>
                {scheduleSearch}
            </div>
            <div id="header" className="schedulenew__search--fixed">
                <div className="schedulenew__search--fixed-width">
                    <div className="schedulenew__statusBar">
                        <div>
                            <label className="input-group--inline" htmlFor="month">
                                <select className="select" id="month" name="month" value={month} onChange={(e) => handleMonthChange(e)}>
                                    <option defaultValue="-1"></option>
                                    {monthOptions.map(month =>
                                        <option key={month.id} value={month.id}>{month.name}</option>
                                    )};
                                </select>
                            </label>
                            <label className="input-group--inline" htmlFor="year">
                                <select className="select" id="year" name="year" value={year} onChange={(e) => handleYearChange(e)}>
                                    <option defaultValue="-1"></option>
                                    {yearOptions.map(year =>
                                        <option key={year.id} value={year.id}>{year.name}</option>
                                    )};
                                </select>
                            </label>
                            <label className="input-group--inline" htmlFor="scheduleDate">
                                <span className="label">Schedule Date</span>
                                <input type='date' id="scheduleDate" name="scheduleDate" className='input' value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)}></input>
                            </label>
                            <label className="input-group--inline" htmlFor="branchFilterId">
                                <span className="label">Branch</span>
                                <select className="select" id="branchFilterId" name="branchFilterId" value={branchFilterId} onChange={(e) => handleBranchChange(e)}>
                                    {scheduleBranchOptions.map(branch =>
                                        <option key={branch.id} value={branch.id}>{branch.name}</option>
                                    )};
                                </select>
                            </label>
                            <button className="defaultbutton defaultbutton__medium margin10" type="button" onClick={refresh}>Refresh</button>
                            <button className="defaultbutton defaultbutton__small margin10" type="button" onClick={resetSearch}>Reset</button>
                        </div>
                        <div className="alignitemscentre">
                            <span>Budget {allowUpdateMonthlyBudget ? "" : monthlyBudgetFormatted}</span>
                            <input className={allowUpdateMonthlyBudget ? "input input--small marginLeft10" : "hidden"} type='number' id="monthlyBudget" name="monthlyBudget" value={monthlyBudget} onChange={(e) => handleBudgetChange(e)} ></input>
                            <span className="marginLeft10">Total {monthAmountFormatted}</span>
                        </div>
                        <div>
                            <button className="defaultbutton margin10" type="button" onClick={search}>Search</button>
                        </div>
                    </div>
                    <div className="marginLeft10">
                        <span className="allocate__customerNoteText">* Items in dark red need Customer unlocked.</span>
                    </div>
                    <div className="marginLeft10">
                        <span className="allocate__customerReckonNoteText">* Items in blue need Customer linked to Reckon.</span>
                    </div>
                    <hr></hr>
                </div>
            </div>
            <div>
                <div className="scheduleNewTable schedulenew__day--margin">
                    <div className={isLoading ? "" : "hidden"}>
                        <p><em>Loading...</em></p>
                    </div>
                    <div className={isLoading ? "hidden" : "scheduleNewTable table__margin overflowAuto"}>

                        {scheduleDays.map((day, index) =>
                            <div key={index} className="scheduleRow paddingTop10">
                                <div id={day.isCurrentDay ? "currentday" : ""} ref={day.isCurrentDay ? scrollRef : undefined}></div>
                                <ScheduleDayData key={index} allowEdit={allowEdit} allowAllocateToPastDate={allowAllocateToPastDate} allowUpdateDayLimits={allowUpdateDayLimits} allowLock={allowLock} allowGetAccountBalance={allowGetAccountBalance} loadingDay={loadingDay} scheduleDay={day} scheduleBranchId={branchFilterId} noteTypes={noteTypes} publicHolidays={publicHolidays} shutdownPeriods={shutdownPeriods} reasonOptions={reasonOptions} scheduleTypes={scheduleTypes} scheduleWarningNo={scheduleWarningNo} scheduleBookedNo={scheduleBookedNo} updateScheduleDay={updateScheduleDay} updateDay={updateDayData} saveDay={saveDayData} saveDayOrder={saveDayOrder} goAllocate={goAllocate} view={viewSearchDay} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            {contents}
        </div>
    );
}

export default ScheduleNewData;